import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Music2, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../context/DataContext";
import { ATTENDANCE_STATUSES } from "../lib/types";
import { getTodaysStudents, todayDay, formatTime12h } from "../lib/schedule";
import { todayStr } from "../lib/progress";

const STATUS_PILL = {
    Present: {
        active: "bg-success text-white border-emerald-500 shadow-sm",
        idle: "bg-white text-success border-success-soft hover:bg-success-soft",
        label: "Present",
        short: "Present",
    },
    Absent: {
        active: "bg-danger text-white border-rose-500 shadow-sm",
        idle: "bg-white text-danger border-danger-soft hover:bg-danger-soft",
        label: "Absent",
        short: "Absent",
    },
    Excused: {
        active: "bg-warning text-white border-warning shadow-sm",
        idle: "bg-white text-warning border-warning-soft hover:bg-warning-soft",
        label: "Excused",
        short: "Excused",
    },
    "Double Session": {
        active: "bg-[#4A6F8F] text-white border-[#4A6F8F] shadow-sm",
        idle: "bg-white text-brand border-brand-soft hover:bg-brand-soft",
        label: "Double Session",
        short: "Double",
    },
};

function INITIALS(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
}

export default function TodaySchedule({
    compact = false,
    filterUnlogged = false,
    autoFocusFirst = false,
}) {
    const { students, attendance, saveAttendance, removeAttendance } = useData();
    const day = todayDay();
    const date = todayStr();
    const allRows = useMemo(
        () => getTodaysStudents(students, day),
        [students, day],
    );

    // Per-student "just logged" flash state, keyed by student id.
    const [flash, setFlash] = useState({});

    // Map of studentId -> current attendance for today (for highlighting).
    const todayByStudent = useMemo(() => {
        const map = {};
        for (const r of attendance) {
            if (r.date === date) map[r.studentId] = r;
        }
        return map;
    }, [attendance, date]);

    // Final rendered rows — may be filtered to unlogged-only.
    const rows = useMemo(() => {
        if (!filterUnlogged) return allRows;
        return allRows.filter(({ student }) => !todayByStudent[student.id]);
    }, [allRows, filterUnlogged, todayByStudent]);

    // Auto-focus the first unlogged row's first button on mount.
    const firstFocusableRef = useRef(null);
    useEffect(() => {
        if (autoFocusFirst && firstFocusableRef.current) {
            firstFocusableRef.current.focus();
            firstFocusableRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [autoFocusFirst]);

    const log = (student, status) => {
        const saved = saveAttendance({
            studentId: student.id,
            date,
            status,
            remark: "",
        });
        setFlash((prev) => ({ ...prev, [student.id]: status }));
        // Sonner: action button on the toast for undo
        toast.success(`${status} · ${student.name}`, {
            action: {
                label: "Undo",
                onClick: () => {
                    if (saved) removeAttendance({ studentId: student.id, date });
                },
            },
        });
        setTimeout(() => {
            setFlash((prev) => {
                const copy = { ...prev };
                delete copy[student.id];
                return copy;
            });
        }, 1400);
    };

    const undo = (student) => {
        removeAttendance({ studentId: student.id, date });
        toast.success(`Attendance undone for ${student.name}`);
    };

    if (students.length === 0) {
        return (
            <div
                className="rounded-2xl border border-dashed border-mist bg-mist/50 p-8 text-center"
                data-testid="today-schedule-no-students"
            >
                <div className="font-display font-bold text-xl uppercase tracking-tight mb-1">
                    No students yet
                </div>
                <div className="text-sm text-slate-500 mb-4">
                    Add your first student to get a live schedule for today.
                </div>
                <div className="flex items-center justify-center">
                    <Link
                        to="/students/new"
                        className="focus-ring inline-flex items-center gap-2 bg-[#3D6385] hover:bg-[#345570] text-white font-bold rounded-xl h-11 px-5"
                        data-testid="schedule-empty-add-student"
                    >
                        Add Student
                    </Link>
                </div>
            </div>
        );
    }

    if (rows.length === 0) {
        // When filterUnlogged is on, this means everything is logged.
        if (filterUnlogged) {
            return (
                <div
                    className="rounded-2xl border border-dashed border-success/30 bg-success-soft/60 p-8 text-center"
                    data-testid="today-schedule-all-logged"
                >
                    <div className="font-display font-bold text-xl uppercase tracking-tight mb-1 text-success">
                        All caught up
                    </div>
                    <div className="text-sm text-success/80">
                        Every student on today’s schedule has been logged.
                    </div>
                </div>
            );
        }
        return (
            <div
                className="rounded-2xl border border-dashed border-mist bg-mist/50 p-8 text-center"
                data-testid="today-schedule-empty-day"
            >
                <div className="font-display font-bold text-xl uppercase tracking-tight mb-1">
                    No lessons scheduled for {day}
                </div>
                <div className="text-sm text-slate-500">
                    Enjoy the breather — or browse your roster below.
                </div>
            </div>
        );
    }

    return (
        <ul
            className={`divide-y divide-mist ${compact ? "" : ""}`}
            data-testid="today-schedule-list"
        >
            {rows.map(({ student, time }, rowIndex) => {
                const logged = todayByStudent[student.id];
                const flashStatus = flash[student.id];
                const activeStatus = flashStatus || logged?.status;
                return (
                    <li
                        key={student.id}
                        className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
                        data-testid={`today-row-${student.id}`}
                    >
                        <div className="flex items-center gap-3 sm:w-56 shrink-0">
                            <div className="w-11 h-11 rounded-xl bg-action-soft text-[#3D6385] font-display font-bold text-sm flex items-center justify-center">
                                {INITIALS(student.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                                <Link
                                    to={`/students/${student.id}`}
                                    className="font-bold text-slate-900 hover:text-[#3D6385] truncate block"
                                    data-testid={`today-student-link-${student.id}`}
                                >
                                    {student.name}
                                </Link>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <Clock size={12} strokeWidth={2.5} />
                                    <span className="font-display font-bold text-slate-700">
                                        {formatTime12h(time) || "—"}
                                    </span>
                                    <span className="text-slate-300">·</span>
                                    <Music2 size={12} strokeWidth={2.5} />
                                    <span>{student.instrument}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
                                {ATTENDANCE_STATUSES.map((s, sIdx) => {
                                    const styles = STATUS_PILL[s];
                                    const isActive = activeStatus === s;
                                    const isLogged = !!logged;
                                    const isDisabled = isLogged && !isActive;
                                    const isFocusTarget =
                                        autoFocusFirst && rowIndex === 0 && sIdx === 0;
                                    return (
                                        <button
                                            key={s}
                                            ref={isFocusTarget ? firstFocusableRef : null}
                                            onClick={() => !isLogged && log(student, s)}
                                            disabled={isDisabled}
                                            data-testid={`quicklog-${student.id}-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                            aria-pressed={isActive}
                                            className={`focus-ring h-10 rounded-xl border-2 text-xs font-bold transition-all ${
                                                isActive
                                                    ? `${styles.active} animate-saved`
                                                    : isDisabled
                                                      ? "bg-white text-slate-300 border-mist opacity-50 cursor-not-allowed"
                                                      : `${styles.idle} active:scale-95`
                                            }`}
                                        >
                                            <span className="inline-flex items-center gap-1">
                                                {isActive && (
                                                    <Check size={13} strokeWidth={3} />
                                                )}
                                                {styles.short}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {logged && (
                                <button
                                    onClick={() => undo(student)}
                                    data-testid={`undo-${student.id}`}
                                    aria-label={`Undo attendance for ${student.name}`}
                                    className="focus-ring h-10 px-3 rounded-xl text-xs font-bold text-slate-500 hover:text-danger hover:bg-danger-soft border-2 border-transparent hover:border-danger-soft transition-all inline-flex items-center justify-center gap-1.5 shrink-0"
                                >
                                    <RotateCcw size={13} strokeWidth={2.8} />
                                    Undo
                                </button>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
